import { useMemo, useState } from 'react';
import { Modal } from '@react-apps/design-system';
import type { PerkPreset } from '../../domain/entities/catalog.entity';
import { usePerkPresetItems } from '../hooks/use-perk-presets';
import { CreatePerkPresetModal } from './CreatePerkPresetModal';
import { PerkPresetItemsEditorModal } from './PerkPresetItemsEditorModal';

interface CreatePerkPresetFromSourceModalProps {
  /** Preset clicado em "Criar novo baseado neste". `null` fecha o fluxo. */
  source: PerkPreset | null;
  onClose: () => void;
}

/**
 * Entrega 2 do editor de associação (§8 do plano): criar preset a partir de
 * outro do mesmo tipo. Mesmas duas etapas do produto — ver
 * `CreateProductFromSourceModal`. `PerkPresetItem` não guarda `overduePolicy`
 * nem `deliversPerCycle` (são do alvo produto); o destino ignora os dois, então
 * os valores aqui são só para completar o shape que o editor espera.
 */
export function CreatePerkPresetFromSourceModal({
  source,
  onClose,
}: CreatePerkPresetFromSourceModalProps) {
  const [createdPreset, setCreatedPreset] = useState<PerkPreset | null>(null);
  const { data: sourceItems = [], isLoading: sourceItemsLoading } =
    usePerkPresetItems(source?.id ?? '');

  const initialAdditions = useMemo(
    () =>
      sourceItems.map((item) => ({
        perkId: item.perkId,
        durationInMilliseconds: item.durationInMilliseconds,
        maxUsages: item.maxUsages,
        renewEveryInMilliseconds: item.renewEveryInMilliseconds,
        targetScopes: item.targetScopes,
        overduePolicy: 'keep_active' as const,
        deliversPerCycle: false,
      })),
    [sourceItems],
  );

  const close = () => {
    setCreatedPreset(null);
    onClose();
  };

  if (!source) return null;

  if (!createdPreset) {
    return (
      <CreatePerkPresetModal
        open
        onClose={close}
        onCreated={setCreatedPreset}
        modalDescription={`Baseado em "${source.name}". O preset nasce vazio; a próxima etapa traz os itens de lá para revisão antes de confirmar.`}
      />
    );
  }

  if (sourceItemsLoading) {
    return (
      <Modal open onClose={close} title="Carregando itens de origem…">
        <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
      </Modal>
    );
  }

  return (
    <PerkPresetItemsEditorModal
      preset={createdPreset}
      open
      onClose={close}
      initialAdditions={initialAdditions}
    />
  );
}
